// tide runs in two modes: standalone (it owns the Cognito session, holds a real
// refresh token and refreshes it itself) and inside mfe_shell as an MFE (the
// shell owns the session, refreshes via amplify and bridges the id_token into
// localStorage on every mount). In shell mode tide's own token lifecycle must
// stand down: it has no real refresh token, so refreshing/wiping/redirecting
// here destroys a session that is actually alive.
import type { ShellStore } from "@observe-tech/marlin-store/contract";
import type {
  Permission,
  ShellHandle,
  ToastLevel,
} from "@observe-tech/mfe-shell-api";

type ShellSessionHandle = {
  permissions?: { raw(): Permission[] };
  auth?: {
    getToken(): Promise<string>;
    currentUser?: { get(): { email?: string } | null };
  };
  nav?: { navigate(path: string, opts?: { replace?: boolean }): void };
  notify?: (message: string, level: ToastLevel) => void;
  offline?: { get(): boolean };
  offlineExpiresAt?: { get(): number | null };
  store?: ShellStore;
  apiVersion?: number;
  cloudConfiguration?: ShellHandle["cloudConfiguration"];
};

export interface OfflineGrant {
  email: string;
  expiresAt: number;
}

export function getShellSession(): ShellSessionHandle | undefined {
  return (window as unknown as { __OBSERVE_SHELL__?: ShellSessionHandle })
    .__OBSERVE_SHELL__;
}

export function isShellSession(): boolean {
  return typeof getShellSession()?.auth?.getToken === "function";
}

export async function getShellIdToken(): Promise<string | null> {
  const shell = getShellSession();
  if (typeof shell?.auth?.getToken !== "function") return null;
  try {
    const token = await shell.auth.getToken();
    return token || null;
  } catch {
    return null;
  }
}

// Standalone tide has no shell to toast through; the caller treats false as
// "nobody was told" rather than as an error.
export function shellNotify(
  message: string,
  level: ToastLevel = "error",
): boolean {
  const notify = getShellSession()?.notify;
  if (typeof notify !== "function") return false;
  notify(message, level);
  return true;
}

export function shellNavigate(path: string): boolean {
  const nav = getShellSession()?.nav;
  if (typeof nav?.navigate !== "function") return false;
  nav.navigate(path);
  return true;
}

// The shell running on a cached snapshot, and the window that snapshot still
// has left. Both halves are required: the flag alone says the cloud is
// unreachable, and a session that outlives its recorded expiry is over whether
// or not the network ever comes back. An older shell exposes neither, which
// reads here as "not offline" and leaves today's behaviour untouched.
export function getOfflineGrant(now: number = Date.now()): OfflineGrant | null {
  const shell = getShellSession();
  if (shell?.offline?.get?.() !== true) return null;
  const expiresAt = shell.offlineExpiresAt?.get?.();
  if (typeof expiresAt !== "number" || now >= expiresAt) return null;
  return { email: shell.auth?.currentUser?.get?.()?.email ?? "", expiresAt };
}
